<template>
    <Teleport to="body">
        <div
            v-show="isOpen"
            class="mobile-categories-panel d-md-none"
            :class="{'mobile-categories-panel--open': isOpen}"
            :aria-hidden="!isOpen"
        >
            <div class="mobile-categories-panel__page">
                <div class="mobile-categories-panel__topbar">
                    <button type="button" class="mobile-categories-panel__back" aria-label="بستن" @click="close">
                        <span class="icon-Chevron_right"></span>
                    </button>
                    <div class="mobile-categories-panel__search">
                        <span class="icon-Search mobile-categories-panel__search-icon"></span>
                        <input
                            v-model="searchQuery"
                            type="search"
                            class="mobile-categories-panel__search-input"
                            placeholder="جستجو در پدینی"
                            autocomplete="off"
                        >
                    </div>
                </div>

                <div v-if="!categories.length" class="mobile-categories-panel__empty">
                    دسته‌بندی‌ای یافت نشد.
                </div>

                <div v-else-if="isSearchMode" class="mobile-categories-panel__search-results">
                    <button
                        v-for="item in searchResults"
                        :key="itemKey(item)"
                        type="button"
                        class="mobile-categories-panel__search-row"
                        @click="navigate(item.url, item.new_tab)"
                    >
                        <span class="mobile-categories-panel__search-label">{{ item.label }}</span>
                        <span v-if="item.pathLabel" class="mobile-categories-panel__search-path">{{ item.pathLabel }}</span>
                    </button>
                    <p v-if="!searchResults.length" class="mobile-categories-panel__empty">موردی یافت نشد</p>
                </div>

                <div v-else class="mobile-categories-panel__split">
                    <aside class="mobile-categories-panel__rail">
                        <button
                            v-for="category in categories"
                            :key="category.id"
                            type="button"
                            class="mobile-categories-panel__rail-item"
                            :class="{'mobile-categories-panel__rail-item--active': activeCategoryId === category.id}"
                            @click="selectCategory(category.id)"
                        >
                            <span
                                class="mobile-categories-panel__rail-icon"
                                :class="categoryIconClass(category.label)"
                            ></span>
                            <span class="mobile-categories-panel__rail-label">{{ category.label }}</span>
                        </button>
                    </aside>

                    <main class="mobile-categories-panel__content" v-if="activeCategory">
                        <h3 class="mobile-categories-panel__section-title">انتخاب {{ activeCategory.label }}</h3>

                        <button
                            v-if="categoryViewAllUrl(activeCategory)"
                            type="button"
                            class="mobile-categories-panel__view-all"
                            @click="navigate(categoryViewAllUrl(activeCategory), activeCategory.new_tab)"
                        >
                            <span>مشاهده همه {{ activeCategory.label }}</span>
                            <span class="icon-Chevron_left"></span>
                        </button>

                        <div class="mobile-categories-panel__groups">
                            <div
                                v-for="sub in activeCategory.children || []"
                                :key="sub.id"
                                class="mobile-categories-panel__group"
                            >
                                <template v-if="hasChildren(sub)">
                                    <button
                                        type="button"
                                        class="mobile-categories-panel__group-head"
                                        :class="{'mobile-categories-panel__group-head--open': isExpanded(sub.id)}"
                                        @click="toggleGroup(sub.id)"
                                    >
                                        <span>{{ sub.label }}</span>
                                        <span class="icon-Chevron_down mobile-categories-panel__group-chevron"></span>
                                    </button>
                                    <ul
                                        v-show="isExpanded(sub.id)"
                                        class="mobile-categories-panel__group-list"
                                    >
                                        <li v-if="categoryViewAllUrl(sub)">
                                            <button
                                                type="button"
                                                class="mobile-categories-panel__group-link mobile-categories-panel__group-link--all"
                                                @click="navigate(categoryViewAllUrl(sub), sub.new_tab)"
                                            >
                                                همه {{ sub.label }}
                                            </button>
                                        </li>
                                        <li v-for="child in sub.children" :key="child.id">
                                            <button
                                                type="button"
                                                class="mobile-categories-panel__group-link"
                                                @click="onSubItemClick(child)"
                                            >
                                                {{ child.label }}
                                            </button>
                                        </li>
                                    </ul>
                                </template>
                                <button
                                    v-else
                                    type="button"
                                    class="mobile-categories-panel__group-head mobile-categories-panel__group-head--link"
                                    @click="onSubItemClick(sub)"
                                >
                                    <span>{{ sub.label }}</span>
                                    <span v-if="sub.url" class="icon-Chevron_left mobile-categories-panel__group-chevron"></span>
                                </button>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
const CATEGORY_ICON_RULES = [
    {match: /موبایل|گوشی|تلفن/i, icon: 'icon-Phone'},
    {match: /لپ.?تاپ|کامپیوتر|دیجیتال/i, icon: 'icon-Frame'},
    {match: /خانه|آشپز|لوازم خان/i, icon: 'icon-Home'},
    {match: /پوشاک|مد|لباس/i, icon: 'icon-Briefcase'},
    {match: /زیبا|آرای/i, icon: 'icon-Star'},
    {match: /ورزش/i, icon: 'icon-Compass'},
    {match: /کتاب|لوازم تح/i, icon: 'icon-Book'},
    {match: /اسباب|بازی|کودک/i, icon: 'icon-Pizza'},
    {match: /ابزار|صنعت/i, icon: 'icon-Settings_cog'},
    {match: /خودرو|موتور/i, icon: 'icon-delivery-truck-1'},
];

export default {
    name: 'MobileCategoriesPanel',
    data() {
        return {
            categories: [],
            activeCategoryId: null,
            searchQuery: '',
            expandedGroups: {},
        };
    },
    computed: {
        isOpen() {
            return this.$store.state.general.categoriesPanelOpen;
        },
        isSearchMode() {
            return !!this.searchQuery.trim();
        },
        activeCategory() {
            return this.categories.find((category) => category.id === this.activeCategoryId) || null;
        },
        searchResults() {
            const query = this.searchQuery.trim().toLowerCase();
            if (!query) {
                return [];
            }

            return this.flattenCategories(this.categories)
                .filter((item) => item.label.toLowerCase().includes(query));
        },
    },
    watch: {
        isOpen(value) {
            if (value) {
                this.loadCategories();
                this.resetPanel();
                document.body.classList.add('noScroll');
                return;
            }
            document.body.classList.remove('noScroll');
        },
    },
    mounted() {
        this.loadCategories();
    },
    methods: {
        loadCategories() {
            const dataEl = document.getElementById('mobile-categories-data');
            if (!dataEl) {
                return;
            }

            try {
                this.categories = JSON.parse(dataEl.textContent || '[]');
            } catch (error) {
                this.categories = [];
            }
        },
        resetPanel() {
            this.searchQuery = '';
            this.expandedGroups = {};
            this.activeCategoryId = this.categories[0]?.id ?? null;
        },
        close() {
            this.$store.commit('general/setCategoriesPanelOpen', false);
        },
        selectCategory(id) {
            this.activeCategoryId = id;
            this.expandedGroups = {};
        },
        hasChildren(item) {
            return Array.isArray(item.children) && item.children.length > 0;
        },
        isExpanded(id) {
            return !!this.expandedGroups[id];
        },
        toggleGroup(id) {
            this.expandedGroups = {
                ...this.expandedGroups,
                [id]: !this.expandedGroups[id],
            };
        },
        categoryIconClass(label) {
            const rule = CATEGORY_ICON_RULES.find(({match}) => match.test(label || ''));
            return rule ? rule.icon : 'icon-Folder_open';
        },
        itemKey(item) {
            return `${item.id || item.label}-${item.url || ''}`;
        },
        flattenCategories(items, path = []) {
            let results = [];

            items.forEach((item) => {
                const currentPath = [...path, item.label];
                const viewAllUrl = this.categoryViewAllUrl(item);

                if (viewAllUrl) {
                    results.push({
                        ...item,
                        url: viewAllUrl,
                        pathLabel: currentPath.length > 1 ? currentPath.slice(0, -1).join(' › ') : '',
                    });
                }

                if (this.hasChildren(item)) {
                    results = results.concat(this.flattenCategories(item.children, currentPath));
                }
            });

            return results;
        },
        categoryViewAllUrl(item) {
            if (!item) {
                return '';
            }

            const url = (item.url || '').trim();
            if (url && url !== '#') {
                return url;
            }

            const firstChild = (item.children || []).find((child) => (child.url || '').trim() && child.url !== '#');
            if (!firstChild) {
                return '';
            }

            const childUrl = firstChild.url.replace(/\/$/, '');
            if (!childUrl.includes('/product-category/')) {
                return '';
            }

            return childUrl.replace(/\/[^/]+$/, '') || '';
        },
        onSubItemClick(item) {
            if (item.url) {
                this.navigate(item.url, item.new_tab);
            }
        },
        navigate(url, newTab = false) {
            if (!url) {
                return;
            }

            this.close();

            if (newTab) {
                window.open(url, '_blank');
                return;
            }

            if (typeof window.navigateToPage === 'function') {
                window.navigateToPage(url);
                return;
            }

            window.location.href = url;
        },
    },
};
</script>
